import { MainframeService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DownloadAssessmentParams, MainframeAssessmentOutput, TcoMainframeAssessment } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Container, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { getId } from '../../common/arn';
import { HelpPanelTopicOptions } from '../../common/Constants';
import { downloadFileFromUrl } from '../../common/DownloadService';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../contexts/PortfolioContext';
import { TcoContext } from '../../contexts/TcoContext';
import BusinessCasePage from './mainframe/BusinessCasePage';
import SavingsSummary from './SavingsSummary';

type Props = FlashContextInterface;

const tcoService = new TcoService();
const mainframeService = new MainframeService();

function MainframeSummary({ flash }: Props) {
    const isMounted = useIsMounted();

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;

    const { assessment, assessmentMetadatas, tcoLoading } = useContext(TcoContext);
    const { portfolio } = useContext(PortfolioContext);

    const [ loading, setLoading ] = useState(false);
    const [ downloading, setDownloading ] = useState(false);
    const [ mainframeAssessment, setMainframeAssessment ] = useState<TcoMainframeAssessment | null>(null);
    const { panelHandler } = useContext(HelpPanelContext);

    const loadMainframeData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const mainframeAssessment = await tcoService.getMainframe(tcoAssessmentId);
                if (isMounted()) {
                    setMainframeAssessment(mainframeAssessment);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_MAINFRAME_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.MAINFRAME_SUMMARY);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadMainframeData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);


    const content = loading ? (<Spinner size='large' />) : mainframeAssessment && mainframeAssessment.output.mainframe.resources.length > 0 && (
        <Box>
            <BusinessCasePage
                mainframe={mainframeAssessment.output.mainframe}
            />
        </Box>
    );

    const getSummaryTotals = (mainframeAssessment: MainframeAssessmentOutput | null, duration: number) => {
        const awsCost = (mainframeAssessment?.awsCosts?.totalCost || 0) * duration;
        const onPremisesCost = (mainframeAssessment?.onPremCosts?.totalCost || 0) * duration;
        const savings = onPremisesCost ? 1 - awsCost / onPremisesCost : 0;
        return {
            awsCost,
            onPremisesCost,
            savings,
        };
    };

    const downloadMainframeAssessment = async (): Promise<void> => {
        setDownloading(true);
        try {
            if (mainframeAssessment?._references) {
                const assessmentMetadata = assessmentMetadatas.find(metadata => metadata.id === tcoAssessmentId);
                const mainframeAssessmentId = getId(mainframeAssessment._references, 'mainframe-assessment') ?? '';
                const params: DownloadAssessmentParams = {
                    timezoneOffset: new Date().getTimezoneOffset(),
                    accountName: portfolio.accountName ?? '',
                    portfolioName: portfolio.name,
                    assessmentName: assessmentMetadata?.name || '',
                };

                const url = await mainframeService.download(mainframeAssessmentId, params);
                downloadFileFromUrl(url);
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_DOWNLOADING_MAINFRAME_ASSESSMENT);
            }
        } finally {
            setDownloading(false);
        }
    };
    
    const savingsSummary = mainframeAssessment && (
        <SavingsSummary
            currency={mainframeAssessment.output.mainframe.currency}
            duration={mainframeAssessment.configuration.tcoAssumptions.durationYears}
            region={mainframeAssessment.configuration.tcoAssumptions.awsRegion}
            summaryTotals={getSummaryTotals(mainframeAssessment.output.mainframe, mainframeAssessment.configuration.tcoAssumptions.durationYears)}
            downloadFn={downloadMainframeAssessment}
            downloadLoading={downloading}
        />
    );

    return tcoLoading ? (
        <Spinner size='large' />
    ) : (
        <SpaceBetween size='l'>
            {savingsSummary}
            <Container>
                <Alert header={constants.MAINFRAME_IS_SEPARATE_FROM_MAIN_TCO}>
                    <span>
                        {constants.MAINFRAME_ALERT_1}<em>{constants.ASSETS} &gt; {constants.MAINFRAMES}</em>
                        {constants.MAINFRAME_ALERT_2}<em>{constants.TCO} &gt; {constants.MAINFRAME_SUMMARY}</em>
                        {constants.MAINFRAME_ALERT_3}
                        {constants.MAINFRAME_ALERT_4}<em>{constants.TCO} &gt; {constants.TCO_SUMMARY}</em>
                        {constants.MAINFRAME_ALERT_5}
                    </span>
                </Alert>
                {content}
            </Container>
        </SpaceBetween>
    );
}

export default withFlashContext(MainframeSummary);
